import { Component, OnDestroy } from '@angular/core';
import * as moment from 'moment';
import { FormGroup, FormControl } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { Subscription, BehaviorSubject, Observable } from 'rxjs';

import { IAccount } from '@account-users';
import {
  JumpstoryStatistics,
  TimeSpanType,
  TimeSpanDropdownItem,
} from '../../models';

import { State } from '../../reducers/jumpstory-statistics.reducer';
import {
  getAllJumpstoryStatistics,
  getLoading,
  getAccounts,
} from '../../reducers';

import { JumpstoryActions } from '../../actions';
import { GetAccounts } from '../../actions/delivery-statistics.actions';

const LAST_DEC_2018 = 1546297199999;
const ONE_YEAR = 31622400000; // 366 days
const SIX_MONTHS = 15897600000 + 3600000; // 184 days + one hour
const THIRTY_DAYS = 2592000000 + 3600000; // 30 days + one hour
const ONE_WEEK = 604800000; // 7 days
const ONE_DAY = 86400000; // 24 hours

@Component({
  selector: 'app-email-jumpstory-statistics',
  templateUrl: './jumpstory-statistics.component.html',
  styleUrls: ['./jumpstory-statistics.component.scss'],
})
export class JumpstoryStatisticsComponent implements OnDestroy {
  includeTrigger$ = new BehaviorSubject<boolean>(false);
  statistics$: Observable<JumpstoryStatistics[]>;

  interval = 'hour';
  accounts: IAccount[] = [];
  filteredAccounts: IAccount[] = [];
  loading = true;
  TimeSpanType = TimeSpanType;

  rangeForm = new FormGroup(
    {
      from: new FormControl({
        year: new Date().getFullYear(),
        month: 1,
        day: 1,
      }),
      to: new FormControl({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
      }),
    },
    (formGroup) => {
      const { from, to } = formGroup.value;
      const momentFrom = moment(
        `${from.year}-${from.month}-${from.day}`,
        'YYYY-MM-DD',
      );
      const momentTo = moment(`${to.year}-${to.month}-${to.day}`, 'YYYY-MM-DD');

      return !(
        momentFrom.isValid() &&
        momentTo.isValid() &&
        momentFrom.isSameOrBefore(momentTo)
      )
        ? { invalidDate: true }
        : null;
    },
  );
  accountControl = new FormControl('');
  filterControl = new FormControl('');
  timeSpanControl = new FormControl(TimeSpanType.ONE_DAY);
  timeValues: TimeSpanDropdownItem[] = [
    { name: 'Last 24 hours', value: TimeSpanType.ONE_DAY },
    { name: 'Last 7 days', value: TimeSpanType.ONE_WEEK },
    { name: 'Last 30 days', value: TimeSpanType.ONE_MONTH },
    { name: 'Last 6 months', value: TimeSpanType.SIX_MONTHS },
    { name: 'Last 12 months', value: TimeSpanType.TWELVE_MONTHS },
    { name: 'All time', value: TimeSpanType.ALL_TIME },
    { name: 'Range', value: TimeSpanType.RANGE },
  ];

  private subscriptions: Subscription[] = [];

  constructor(private readonly store: Store<State>) {
    this.statistics$ = this.store.pipe(select(getAllJumpstoryStatistics));

    this.store.dispatch(new GetAccounts({ searchTerm: '', loadAll: true }));

    this.subscriptions = [
      this.store
        .pipe(select(getLoading))
        .subscribe((loading) => (this.loading = loading)),

      this.store.pipe(select(getAccounts)).subscribe((accounts) => {
        this.accounts = accounts;
        this.filteredAccounts = accounts;
      }),

      this.filterControl.valueChanges.subscribe(
        (value) =>
          (this.filteredAccounts = this.accounts.filter((a) =>
            new RegExp(value, 'i').test(`${a.displayName}${a.id}`),
          )),
      ),
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  fetchStatistics() {
    const accountId = this.accountControl.value;
    const timeSpanType = this.timeSpanControl.value;

    if (timeSpanType === TimeSpanType.RANGE && this.rangeForm.invalid) {
      return;
    }

    const timeSpans = this.getTimeSpans(timeSpanType);

    accountId
      ? this.store.dispatch(
          JumpstoryActions.fetchByAccountRequest({ accountId, timeSpans }),
        )
      : this.store.dispatch(JumpstoryActions.fetchRequest({ timeSpans }));
  }

  getFromToTimeStamps(
    endOfToken: 'hour' | 'day',
    subtractAmount: number,
    subtractToken: 'hour' | 'day' | 'month',
  ) {
    return {
      from: moment()
        .endOf(endOfToken)
        .subtract(subtractAmount, subtractToken)
        .valueOf(),
      to: moment().endOf(endOfToken).valueOf(),
    };
  }

  getFromTo(type: TimeSpanType) {
    switch (type) {
      case TimeSpanType.ONE_DAY:
        return this.getFromToTimeStamps('hour', 24, 'hour');
      case TimeSpanType.ONE_WEEK:
        return this.getFromToTimeStamps('day', 7, 'day');
      case TimeSpanType.ONE_MONTH:
        return this.getFromToTimeStamps('day', 30, 'day');
      case TimeSpanType.SIX_MONTHS:
        return this.getFromToTimeStamps('day', 6, 'month');
      case TimeSpanType.TWELVE_MONTHS:
        return this.getFromToTimeStamps('day', 12, 'month');
      case TimeSpanType.ALL_TIME:
        return {
          from: LAST_DEC_2018,
          to: moment().endOf('day').valueOf(),
        };
      case TimeSpanType.RANGE: {
        const { from, to } = this.rangeForm.value;
        return {
          from: moment(`${from.year}-${from.month}-${from.day}`, 'YYYY-MM-DD')
            .startOf('day')
            .subtract(1, 'millisecond')
            .valueOf(),
          to: moment(`${to.year}-${to.month}-${to.day}`, 'YYYY-MM-DD')
            .endOf('day')
            .valueOf(),
        };
      }
    }
  }

  getInterval(from: number, to: number) {
    const diff = to - from;
    switch (true) {
      case diff > ONE_YEAR:
        return 'year';
      case diff > SIX_MONTHS:
        return 'month';
      case diff > THIRTY_DAYS:
        return 'week';
      case diff > ONE_WEEK:
      case diff > ONE_DAY:
        return 'day';
      default:
        return 'hour';
    }
  }

  getTimestamps(
    from: number,
    to: number,
    interval: 'year' | 'month' | 'week' | 'day' | 'hour',
  ) {
    const isoInterval = interval === 'week' ? 'isoWeek' : interval;
    const plusOne = (timestamp: number) =>
      moment(timestamp).add(1, interval).startOf(isoInterval).valueOf();

    const timestamps = [];

    for (let step = plusOne(from); step < to; step = plusOne(step)) {
      if (step - from > 1) {
        timestamps.push(step);
      }
    }

    return [from, ...timestamps, moment(to).add(1, 'millisecond').valueOf()];
  }

  getTimeSpans(type: TimeSpanType): number[] {
    const { from, to } = this.getFromTo(type);
    const interval = this.getInterval(from, to);
    this.interval = interval;

    return this.getTimestamps(from, to, interval);
  }

  resetFilters() {
    this.accountControl.setValue('');
    this.filterControl.setValue('');
    this.timeSpanControl.setValue(TimeSpanType.ONE_DAY);
  }

  openJumpstoryImage({ imageUrl }) {
    window.open(imageUrl, '_blank');
  }

  downloadCSV() {
    const accountId = this.accountControl.value;
    const timeSpanType = this.timeSpanControl.value;

    if (timeSpanType === TimeSpanType.RANGE && this.rangeForm.invalid) {
      return;
    }

    const timeSpans = this.getTimeSpans(timeSpanType);

    this.store.dispatch(
      JumpstoryActions.downloadCSVRequest({ accountId, timeSpans }),
    );
  }
}
